import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useState } from 'react';

import { useSocket } from './useSocket';

/**
 * Subscribes to inbox Socket.io events for the current organization.
 *
 * - `inbox:new_message`   → adds message to the top of the conversation
 *   list cache and increments the unread badge.
 * - `inbox:message_status` → updates the delivery status ticks in the
 *   currently-open thread.
 */
export function useInboxRealtime(): {
  unreadCount: number;
} {
  const { on, off } = useSocket();
  const qc = useQueryClient();
  const [unreadCount, setUnreadCount] = useState(0);

  const onNewMessage = useCallback(
    (raw: unknown) => {
      // Backend RealtimeBridge.onInboxMessagePersisted forwards the
      // `inbox.message.persisted` payload verbatim to the socket, so
      // the wire shape is snake_case (conversation_id) — NOT camelCase.
      // The previous `as InboxNewMessageEvent` cast claimed
      // `conversationId` and silently produced `undefined`, so the
      // invalidate target never matched any active query and the
      // open thread never refetched. Read both casings defensively.
      const event = raw as Record<string, unknown> | null;
      const convId =
        (typeof event?.conversation_id === 'string' && event.conversation_id) ||
        (typeof event?.conversationId === 'string' && event.conversationId) ||
        null;
      setUnreadCount((c) => c + 1);
      // A12 follow-up — queryKeys are now
      // `['inbox','conversations',projectId,...]` and
      // `['inbox','messages',projectId,convId]`. React Query matches by
      // prefix, so invalidating the shared 2-element prefix catches every
      // project/conversation combination in the cache.
      void qc.invalidateQueries({ queryKey: ['inbox', 'conversations'] });
      if (convId) {
        void qc.invalidateQueries({ queryKey: ['inbox', 'messages'] });
        // Also refresh the OPEN thread's detail (singular key). windowOpen is
        // derived from selectedConv.last_inbound_message_at, so without this a
        // customer's reply lands but the 24h composer stays locked until reload.
        void qc.invalidateQueries({ queryKey: ['inbox', 'conversation'] });
      }
    },
    [qc],
  );

  const onStatusChange = useCallback(
    (raw: unknown) => {
      // Wire shape (from RealtimeBridge.onInboxStatusAdvanced) is
      // `{ project_id, message_id, wa_message_id, status, timestamp, failed_reason? }`
      // — `status` is a STRING, not an object. The old code read
      // `event.status.conversation_id`, which is always undefined, so the
      // refetch never fired and delivery/read/FAILED ticks stayed frozen on
      // their initial value in the open thread. Refetch on any status event.
      const event = raw as { message_id?: unknown } | null;
      if (typeof event?.message_id !== 'string') return;
      // Refresh only the open thread's ticks. We intentionally do NOT
      // invalidate the conversation list here: status moves through
      // sent→delivered→read for every outbound message, and the list shows
      // last_message_preview (unchanged by status), so refetching it on every
      // tick would storm the list endpoint during broadcasts.
      void qc.invalidateQueries({ queryKey: ['inbox', 'messages'] });
    },
    [qc],
  );

  // A reaction was applied/removed on a message (by another agent or this
  // user in another tab). Refresh the open thread so the bubble's reaction
  // chip updates without a manual reload.
  const onReaction = useCallback(
    (raw: unknown) => {
      const event = raw as { conversation_id?: string } | null;
      if (event?.conversation_id) {
        void qc.invalidateQueries({ queryKey: ['inbox', 'messages'] });
      }
    },
    [qc],
  );

  // P4·S10: sentiment updates from `ConversationSentimentService`
  // bubble through `conversation:sentiment_updated`. Invalidating both
  // queries keeps the conversation-list badges and the open thread
  // header in sync.
  const onSentimentUpdated = useCallback(
    (raw: unknown) => {
      const event = raw as { conversation_id: string };
      void qc.invalidateQueries({ queryKey: ['inbox', 'conversations'] });
      if (event.conversation_id) {
        // Prefix match across `['inbox','conversation',projectId,convId]`.
        void qc.invalidateQueries({ queryKey: ['inbox', 'conversation'] });
      }
    },
    [qc],
  );

  useEffect(() => {
    on('inbox:new_message', onNewMessage);
    on('inbox:message_status', onStatusChange);
    on('inbox:message_reaction', onReaction);
    on('conversation:sentiment_updated', onSentimentUpdated);
    return () => {
      off('inbox:new_message', onNewMessage);
      off('inbox:message_status', onStatusChange);
      off('inbox:message_reaction', onReaction);
      off('conversation:sentiment_updated', onSentimentUpdated);
    };
  }, [on, off, onNewMessage, onStatusChange, onReaction, onSentimentUpdated]);

  return { unreadCount };
}
